import useAiAssistantWidget from '../../hooks/useAiAssistantWidget';
import useIsNavigationBarVisible from '../../hooks/useIsNavigationBarVisible';
import useLastDefined from '../../hooks/useLastDefined';
import usePresence from '../../hooks/usePresence';
import aiAssistantWidgetHelpers from '../../services/aiAssistant/aiAssistantWidgetHelpers';
import {twx} from '../../utils/tailwindUtils';
import AiAssistantWidgetCard from './AiAssistantWidgetCard/AiAssistantWidgetCard';
import AiAssistantWidgetConversation from './AiAssistantWidgetConversation/AiAssistantWidgetConversation';
import AiAssistantWidgetPill from './AiAssistantWidgetPill';

// Longest exit transition: the scrim's delay-[60ms] + duration-[400ms]
const EXIT_MS = 500;
const LAYER_CLASS = 'pointer-events-none fixed inset-x-0 z-[130] mx-auto max-w-md origin-top';

const AiAssistantWidget = () => {
    const {isWidgetOpened, assistantState, proposal, closeWidget, confirmProposal, dismissProposal} =
        useAiAssistantWidget();
    // The nav bar tab is the only way to close the widget, so it hides together with the bar
    const isNavigationBarVisible = useIsNavigationBarVisible();
    // Keeps the card filled while it fades out after the proposal is cleared
    const cardProposal = useLastDefined(proposal);
    // CSS transitions, not JS: Safari runs them off the main thread
    const {isMounted, isShown} = usePresence(isWidgetOpened && isNavigationBarVisible, EXIT_MS);

    if (!isMounted) return null;

    const card = cardProposal && (
        <AiAssistantWidgetCard
            proposal={cardProposal}
            footer={aiAssistantWidgetHelpers.getCardFooter(assistantState, cardProposal)}
            getSettingValue={(settingName) =>
                aiAssistantWidgetHelpers.getProposalSettingValue(cardProposal, settingName)
            }
            onConfirm={confirmProposal}
            onAutoClose={dismissProposal}
            onClose={closeWidget}
        />
    );

    return (
        <>
            {/* Scrim */}
            <div
                className={twx(
                    LAYER_CLASS,
                    'top-0 h-[579px] bg-[linear-gradient(180deg,#121212_0%,rgba(18,18,18,0)_100%)]',
                    'transition-[opacity,transform] ease-out',
                    isShown ? 'duration-500' : 'opacity-0 delay-[60ms] duration-[400ms] motion-safe:scale-y-[0.6]'
                )}
            />

            {/* Grows out of the Dynamic Island */}
            <div
                className={twx(
                    LAYER_CLASS,
                    'top-16 transition-transform',
                    // ~3% overshoot, like SwiftUI's .bouncy
                    isShown
                        ? 'duration-500 ease-[cubic-bezier(0.34,1.3,0.64,1)]'
                        : 'duration-300 ease-in motion-safe:-translate-y-14 motion-safe:scale-50'
                )}
            >
                <AiAssistantWidgetPill
                    isFaded={!isShown}
                    isExpanded={aiAssistantWidgetHelpers.getIsCardShown(proposal)}
                    collapsedBody={<AiAssistantWidgetConversation assistantState={assistantState} />}
                    expandedBody={card}
                />
            </div>
        </>
    );
};

export default AiAssistantWidget;
